/**
 * useColumnConfig — hook con cache por-cliente para la config de COLUMNAS.
 *
 * La ficha de Data Health necesita el `ColumnConfigDoc` para listar los campos
 * como los ve el usuario en Publicaciones. Para no re-fetchear en cada apertura
 * de ficha (una por fila), cacheamos por clientId a nivel de módulo y deduplicamos
 * requests en vuelo.
 */
import { useEffect, useState } from 'react';
import { getColumnConfig, type ColumnConfigDoc } from '../../../services/columnConfig';

const cache = new Map<string, ColumnConfigDoc>();
const inflight = new Map<string, Promise<ColumnConfigDoc>>();

export function useColumnConfig(clientId: string): { config: ColumnConfigDoc | null; loading: boolean } {
  const [config, setConfig] = useState<ColumnConfigDoc | null>(() => cache.get(clientId) ?? null);
  const [loading, setLoading] = useState<boolean>(() => !cache.has(clientId));

  useEffect(() => {
    let cancelled = false;

    const cached = cache.get(clientId);
    if (cached) {
      setConfig(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    let p = inflight.get(clientId);
    if (!p) {
      p = getColumnConfig(clientId)
        .then((r) => {
          cache.set(clientId, r.columnConfig);
          return r.columnConfig;
        })
        .finally(() => {
          inflight.delete(clientId);
        });
      inflight.set(clientId, p);
    }

    p.then((cfg) => {
      if (!cancelled) {
        setConfig(cfg);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return { config, loading };
}
